import {
    AfterViewChecked,
    ApplicationRef,
    ChangeDetectorRef,
    Component,
    ComponentFactoryResolver,
    ComponentRef,
    ElementRef,
    Injectable,
    Injector,
    NgZone,
    OnDestroy,
    OnInit,
    Type,
    ViewChild,
    ViewContainerRef,
} from '@angular/core';
import {
    animationFrameScheduler,
    from,
    fromEvent,
    merge,
    of,
    race,
    Subject,
    Subscription,
} from 'rxjs';
import {
    auditTime,
    catchError,
    filter,
    finalize,
    takeUntil,
    tap,
} from 'rxjs/operators';

const TRIANGLE_WIDTH = 20;
const TRIANGLE_HEIGHT = 10;

type Placement =
    | 'auto'
    | 'top-auto'
    | 'right-auto'
    | 'bottom-auto'
    | 'left-auto'
    | 'top-center'
    | 'right-center'
    | 'bottom-center'
    | 'left-center'
    | 'top-left'
    | 'top-right'
    | 'right-top'
    | 'right-bottom'
    | 'bottom-left'
    | 'bottom-right'
    | 'left-top'
    | 'left-bottom';

interface Style {
    [p: string]: string;

    visibility: string;
    top?: string;
    left?: string;
}

export interface Pos {
    placement?: Placement;
    style: Style;
}

function doPlacement(
    placement: Placement,
    elementRect: ClientRect,
    targetRect: ClientRect,
    scrollTop: number,
    scrollLeft: number,
    sidesAttempted: Placement[],
    triangle?: boolean,
): { nextPlacement: Placement; top: number; left: number } {
    let [primary, secondary] = (
        placement === 'auto' ? 'top-center' : placement
    ).split('-');
    secondary = secondary === 'auto' ? 'center' : secondary;
    const padWidth = !triangle ? 0 : TRIANGLE_WIDTH;
    const padHeight = !triangle ? 0 : TRIANGLE_HEIGHT;
    let top;
    let left;

    switch (primary) {
        case 'top':
            top = scrollTop + targetRect.top - elementRect.height - padHeight;
            break;
        case 'right':
            top =
                scrollTop +
                targetRect.top +
                targetRect.height / 2 -
                elementRect.height / 2;
            break;
        case 'bottom':
            top = scrollTop + targetRect.top + targetRect.height + padHeight;
            break;
        case 'left':
            if (secondary === 'top') {
                top = scrollTop + targetRect.top;
            }
            if (secondary === 'center') {
                top =
                    scrollTop +
                    targetRect.top +
                    targetRect.height / 2 -
                    elementRect.height / 2;
            }
            break;
    }

    switch (secondary) {
        case 'top':
            if (primary === 'left') {
                left =
                    scrollLeft + targetRect.left - elementRect.width - padWidth;
            }
            break;
        case 'right':
            left =
                scrollLeft +
                targetRect.left +
                targetRect.width -
                elementRect.width;
            break;
        case 'left':
            left = scrollLeft + targetRect.left;
            break;
        case 'center':
            if (primary === 'top') {
                left =
                    scrollLeft +
                    targetRect.left +
                    targetRect.width / 2 -
                    elementRect.width / 2;
            }
            if (primary === 'right') {
                left =
                    scrollLeft + targetRect.left + targetRect.width + padWidth;
            }
            if (primary === 'bottom') {
                left =
                    scrollLeft +
                    targetRect.left +
                    targetRect.width / 2 -
                    elementRect.width / 2;
            }
            if (primary === 'left') {
                left =
                    scrollLeft + targetRect.left - elementRect.width - padWidth;
            }
            break;
    }

    const result = {
        nextPlacement: `${primary}-${secondary}` as Placement,
        top,
        left,
    };
    const nextAttempt = (sideAtFault: string): Placement => {
        const primaryIsAuto = placement.startsWith('auto');
        let nextStr: string = null;
        if (primaryIsAuto) {
            if (sideAtFault === 'top') {
                nextStr = `bottom-${secondary}`;
            }
            if (sideAtFault === 'right') {
                nextStr = `left-${secondary}`;
            }
            if (sideAtFault === 'left') {
                nextStr = `right-${secondary}`;
            }
        }
        if (!primaryIsAuto) {
            if (primary === 'top' || primary === 'bottom') {
                if (sideAtFault === 'left') {
                    nextStr = `${primary}-left`;
                }
                if (sideAtFault === 'right') {
                    nextStr = `${primary}-right`;
                }
            }
            if (primary === 'right' || primary === 'left') {
                if (sideAtFault === 'top') {
                    nextStr = `${primary}-bottom`;
                }
                if (sideAtFault === 'bottom') {
                    nextStr = `${primary}-top`;
                }
            }
        }
        let nextPlacement = nextStr as Placement;
        // eslint-disable-next-line @typescript-eslint/prefer-includes
        if (nextPlacement && sidesAttempted.indexOf(nextPlacement) === -1) {
            sidesAttempted.push(nextPlacement);
        } else {
            nextPlacement = null;
        }
        return nextPlacement;
    };
    let next: Placement;
    if (top < scrollTop) {
        next = nextAttempt('top');
    }
    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
    if (
        !next &&
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        left + elementRect.width > document.documentElement.clientWidth
    ) {
        next = nextAttempt('right');
    }
    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
    if (
        !next &&
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        top + elementRect.height > document.documentElement.clientHeight
    ) {
        next = nextAttempt('bottom');
    }
    if (!next && left < 0) {
        next = nextAttempt('left');
    }

    let nextResult = null;
    if (next) {
        nextResult = doPlacement(
            next,
            elementRect,
            targetRect,
            scrollTop,
            scrollLeft,
            sidesAttempted,
            triangle,
        );

        // check if doPlacement failed
        if (
            nextResult.top != null &&
            !isNaN(nextResult.top) &&
            nextResult.left != null &&
            !isNaN(nextResult.left)
        ) {
            result.nextPlacement = nextResult.nextPlacement;
            result.top = nextResult.top;
            result.left = nextResult.left;
        }
    }

    return result;
}

export function getPos(
    element: HTMLElement,
    target: HTMLElement,
    placement: Placement = 'auto',
    triangle?: boolean,
): Pos {
    if (!element || !target) return { style: { visibility: 'none' } };
    const elementRect = element.getBoundingClientRect();
    if (elementRect.width === 0) return { style: { visibility: 'none' } };
    const targetRect = target.getBoundingClientRect();
    const scrollTop =
        window.scrollY ||
        window.pageYOffset ||
        document.documentElement.scrollTop;
    const scrollLeft =
        window.scrollX ||
        window.pageXOffset ||
        document.documentElement.scrollLeft;
    const sidesAttempted: Placement[] = [];
    const { nextPlacement, top, left } = doPlacement(
        placement,
        elementRect,
        targetRect,
        scrollTop,
        scrollLeft,
        sidesAttempted,
        triangle,
    );
    return {
        placement: nextPlacement,
        style: { visibility: 'visible', top: `${top}px`, left: `${left}px` },
    };
}

export interface Options {
    target: HTMLElement | (() => HTMLElement);
    placement?: Placement;
    triangle?: boolean;
    containerClass?: string;
    backdrop?: boolean;
    backdropClass?: string;
    backdropDismiss?: boolean;
    relativeTo?: () => HTMLElement;
    autoClose?: boolean | string; // click | escape | mouseleave
    injector?: Injector;
}

export interface PopUpState {
    hidden: boolean;
    pos?: Pos;
    autoClose?: string[];
}

@Component({
    selector: '[pop-up]',
    template: ` <div
            class="pu-container"
            [ngClass]="props?.containerClass"
            [ngStyle]="state?.pos?.style"
        >
            <ng-container #ref></ng-container>
            <div
                class="pu-triangle"
                [ngClass]="translatePlacement"
                *ngIf="props?.triangle"
            ></div>
        </div>
        <div
            class="pu-backdrop"
            style="display: none;"
            [ngClass]="props?.backdropClass"
            (click)="props?.backdropDismiss && dismiss()"
            #backdrop
        ></div>`,
})
export class PopUpComponent<T> implements OnInit, AfterViewChecked, OnDestroy {
    @ViewChild('ref', { read: ViewContainerRef, static: true })
    viewContainerRef: ViewContainerRef;

    @ViewChild('backdrop', { static: true })
    private readonly _backdropRef: ElementRef;

    componentRef: ComponentRef<T>;
    private _done: Subject<void>;
    private _props: Options;
    state: PopUpState;
    result: Promise<any>;
    private _resolve: (reason?: any) => void;
    private _reject: (reason?: any) => void;
    private _didAutoCloseBind: boolean;

    set props(value: Options) {
        this._props = value;

        if (this.props.autoClose) {
            if (typeof this.props.autoClose === 'string') {
                this.state.autoClose = this.props.autoClose.split('|');
            } else if (this.props.autoClose === true) {
                this.state.autoClose = ['click', 'escape'];
            }

            if (!this._didAutoCloseBind) {
                this._didAutoCloseBind = true;

                this._ngZone.runOutsideAngular(() => {
                    const ms = fromEvent<MouseEvent>(document, 'mouseup').pipe(
                        takeUntil(this._done),
                        filter((e) => e.button === 0),
                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        filter(
                            () =>
                                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                                this.state.autoClose &&
                                this.state.autoClose.some((e) => e === 'click'),
                        ),
                        filter(
                            (e) => !this._el.nativeElement.contains(e.target),
                        ),
                    );
                    const kb = fromEvent<KeyboardEvent>(
                        document,
                        'keydown',
                    ).pipe(
                        takeUntil(this._done),
                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        filter(
                            () =>
                                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                                this.state.autoClose &&
                                this.state.autoClose.some(
                                    (e) => e === 'escape',
                                ),
                        ),
                        filter((e) => e.which === 27),
                    );
                    race([ms, kb])
                        .pipe(tap(() => this.dismiss()))
                        .subscribe();

                    fromEvent(
                        typeof this.props.target === 'function'
                            ? this.props.target()
                            : this.props.target,
                        'mouseleave',
                    )
                        .pipe(
                            takeUntil(this._done),
                            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                            filter(
                                () =>
                                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                                    this.state.autoClose &&
                                    this.state.autoClose.some(
                                        (e) => e === 'mouseleave',
                                    ),
                            ),
                            tap((e) => {
                                e.stopPropagation();
                                this.dismiss();
                            }),
                        )
                        .subscribe();
                });
            }
        }

        if (this.props.backdrop) {
            this.relativeTo.appendChild(this._backdropRef.nativeElement);
            this._adjustBackdrop(this.props.backdrop);
        }
    }

    get props(): Options {
        return this._props;
    }

    get translatePlacement(): string {
        if (!this.state.pos || !this.state.pos.placement) return null;
        const [primary, secondary] = this.state.pos.placement.split('-');
        return `${primary} ${primary}-${secondary}`;
    }

    get relativeTo(): HTMLElement {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        return (
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            (this.props.relativeTo && this.props.relativeTo()) || document.body
        );
    }

    constructor(
        private readonly _ngZone: NgZone,
        private readonly _el: ElementRef,
        private readonly _changeDetectorRef: ChangeDetectorRef,
    ) {
        this.state = { hidden: false };

        this._done = new Subject();
        this.result = new Promise<any>((resolve, reject) => {
            this._resolve = resolve;
            this._reject = reject;
        });
    }

    ngOnInit(): void {
        this._ngZone.runOutsideAngular(() => {
            merge(fromEvent(window, 'resize'), fromEvent(window, 'scroll'))
                .pipe(
                    takeUntil(this._done),
                    auditTime(0, animationFrameScheduler),
                    tap(() => this._adjustPosition()),
                )
                .subscribe();
        });
    }

    ngAfterViewChecked(): void {
        if (this.viewContainerRef.length === 0) return;

        this._adjustPosition();
    }

    ngOnDestroy(): void {
        this._adjustBackdrop(false);
        this._el.nativeElement.appendChild(this._backdropRef.nativeElement);
        this._done.next();
        this._done = null;
    }

    dismiss(reason?: any): void {
        this._reject(reason);
    }

    close(reason?: any): void {
        this._resolve(reason);
    }

    private _adjustPosition(): void {
        this._el.nativeElement.style.position = 'absolute';
        this._el.nativeElement.style.width = '100%';
        this._el.nativeElement.style.top = '0';
        this._el.nativeElement.style.left = '0';
        // this._el.nativeElement.style.overflow = 'hidden';
        const element = this.componentRef.location.nativeElement;
        const prev = this.state.pos;
        const next =
            this.props.target &&
            getPos(
                element,
                typeof this.props.target === 'function'
                    ? this.props.target()
                    : this.props.target,
                this.props.placement,
                this.props.triangle,
            );
        if (
            !prev ||
            !prev.style ||
            // eslint-disable-next-line @typescript-eslint/dot-notation
            prev.style['top'] !== next.style['top'] ||
            // eslint-disable-next-line @typescript-eslint/dot-notation
            prev.style['left'] !== next.style['left']
        ) {
            this.state.pos = next;
            this._changeDetectorRef.detectChanges();
        }
        this._el.nativeElement.style.position = '';
        this._el.nativeElement.style.width = '';
        this._el.nativeElement.style.top = '';
        this._el.nativeElement.style.left = '';
        // this._el.nativeElement.style.overflow = '';
    }

    private _adjustBackdrop(apply: boolean): void {
        if (!apply) {
            this._backdropRef.nativeElement.style.display = 'none';
            this.relativeTo.classList.remove('pu-backdrop-parent');
        } else {
            this._backdropRef.nativeElement.style.display = 'block';
            this.relativeTo.classList.add('pu-backdrop-parent');
        }
    }
}

@Injectable()
export class PopUpService {
    private _wrapperRef: ComponentRef<PopUpComponent<any>>;
    private _onResult: Subscription;

    get active(): PopUpComponent<any> {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        return this._wrapperRef && this._wrapperRef.instance;
    }

    constructor(
        private readonly _factoryResolver: ComponentFactoryResolver,
        private readonly _injector: Injector,
        private readonly _applicationRef: ApplicationRef,
    ) {}

    show<T>(component: Type<T>, opts: Options): PopUpComponent<T> {
        this.hide();

        const injector = opts.injector
            ? Injector.create({ providers: [], parent: opts.injector })
            : this._injector;
        const factory =
            this._factoryResolver.resolveComponentFactory(PopUpComponent);
        this._wrapperRef = factory.create(injector);
        this._wrapperRef.hostView.detectChanges();
        this._applicationRef.attachView(this._wrapperRef.hostView);
        document.body.appendChild(this._wrapperRef.location.nativeElement);

        this._wrapperRef.instance.componentRef =
            this._wrapperRef.instance.viewContainerRef.createComponent(
                this._factoryResolver.resolveComponentFactory(component),
            );
        this._wrapperRef.instance.props = opts;
        this._onResult = from(this._wrapperRef.instance.result)
            .pipe(
                catchError(() => of(null)),
                finalize(() => this.hide()),
            )
            .subscribe();

        return this._wrapperRef.instance as PopUpComponent<T>;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    hide<T>(): void {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._onResult && this._onResult.unsubscribe();
        if (this._wrapperRef) {
            if (this._wrapperRef.instance) {
                this._wrapperRef.instance.dismiss();
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                this._wrapperRef.instance.componentRef &&
                    this._wrapperRef.instance.componentRef.destroy();
            }
            this._applicationRef.detachView(this._wrapperRef.hostView);
            this._wrapperRef.destroy();
        }
        this._wrapperRef = null;
    }
}
